/**
 * filter_bar.js — DAS-78 Phase 3.5 · 위험 상태 칩 필터 바
 *
 * 시안 V1 FilterBar(`ClaudeDesignDashboard/pages/dashboard.jsx::FilterBar` L207~253)
 * 의 **상태 칩 부분만** 운영에 추가. 정렬/검색은 PositionTable 내부 그대로 유지(D3 (c) 하이브리드).
 *
 * 5개 상태 칩 — danger / high / caution / watch / safe
 *   - StatusDot(tone) + 한국어 라벨 + 카운트(mono 11px content-muted)
 *   - 토글 가능. 선택 시 brand-subtle 배경 + brand 35% 보더로 강조
 *   - 빈 selectedStatuses(=Set 크기 0)는 "모든 상태 표시" 의미
 *
 * 디자인 시스템 토큰 전용. raw hex 금지. JetBrains Mono 유지(font-num).
 * 시안 매핑(POSITION_STATUS_META):
 *   danger  → 청산위험 / loss
 *   high    → 고위험   / warning
 *   caution → 주의     / warning
 *   watch   → 관찰     / brand
 *   safe    → 안정     / gain
 *
 * @example
 *   <FilterBar
 *     selectedStatuses={new Set(['danger', 'high'])}
 *     onToggleStatus={(key) => toggle(key)}
 *     counts={{ danger: 1, high: 2, caution: 0, watch: 4, safe: 8 }}
 *   />
 */

import React from 'react';
import { StatusDot } from './claude_primitives';

/** 상태 키 + 메타데이터 (시안 dashboard.jsx::STATUSES 와 1:1 매핑, 한국어 라벨로 번역). */
export const FILTER_BAR_STATUSES = [
    { key: 'danger',  label: '청산위험', tone: 'loss' },
    { key: 'high',    label: '고위험',   tone: 'warning' },
    { key: 'caution', label: '주의',     tone: 'warning' },
    { key: 'watch',   label: '관찰',     tone: 'brand' },
    { key: 'safe',    label: '안정',     tone: 'gain' },
];

/** position_table.js::getRiskLevel().priority → FilterBar 상태 키 매핑.
 *  매핑 표: 0→danger / 1→high / 2→caution / 3→watch / 4→safe / 5→safe(정보없음 통합).
 */
export const PRIORITY_TO_STATUS_KEY = {
    0: 'danger',
    1: 'high',
    2: 'caution',
    3: 'watch',
    4: 'safe',
    5: 'safe',
};

/**
 * priority 별 카운트({ danger, high, caution, watch, safe })를 계산한다.
 * @param {Array<{ priority?: number }>} rows — priority 필드를 가진 row 배열
 */
export function buildPositionStatusCounts(rows) {
    const counts = { danger: 0, high: 0, caution: 0, watch: 0, safe: 0 };
    if (!Array.isArray(rows)) return counts;
    rows.forEach((row) => {
        const p = Number(row?.priority);
        const key = PRIORITY_TO_STATUS_KEY[p];
        if (key) counts[key] += 1;
    });
    return counts;
}

/**
 * FilterBar — 위험 상태 칩 5개 토글 그룹.
 *
 * @param {object}        props
 * @param {Set<string>}   props.selectedStatuses    선택된 상태 키 Set (빈 Set = 전체)
 * @param {(key:string)=>void} props.onToggleStatus 칩 클릭 시 호출 (상태 키 전달)
 * @param {object}        props.counts              { danger, high, caution, watch, safe }
 * @param {string}        [props.className]         추가 wrapper 클래스
 */
const FilterBar = ({ selectedStatuses, onToggleStatus, counts, className = '' }) => {
    const safeSelected = selectedStatuses instanceof Set ? selectedStatuses : new Set();
    const safeCounts = counts && typeof counts === 'object' ? counts : {};

    return (
        <div
            className={`flex flex-wrap items-center gap-1.5 ${className}`}
            role="group"
            aria-label="위험 상태 필터"
        >
            <span className="mr-1 text-xs font-semibold uppercase tracking-wider text-content-muted">
                상태
            </span>
            {FILTER_BAR_STATUSES.map((s) => {
                const on = safeSelected.has(s.key);
                const count = Number(safeCounts[s.key]) || 0;

                /* 활성 시: brand 35% border + brand-subtle bg + content
                 * 비활성: surface-border + 투명 bg + content-secondary
                 *   — 시안 dashboard.jsx FilterBar 'pill on' 패턴과 디자인 시스템(filter_pill.js) 사이의 균형. */
                const stateClasses = on
                    ? 'bg-brand-subtle text-content border-brand/35 hover:bg-brand-subtle'
                    : 'bg-transparent text-content-secondary border-surface-border hover:bg-surface-hover hover:text-content';

                return (
                    <button
                        key={s.key}
                        type="button"
                        onClick={() => onToggleStatus && onToggleStatus(s.key)}
                        aria-pressed={on}
                        aria-label={`${s.label} 필터 — ${count}건${on ? ', 선택됨' : ''}`}
                        className={[
                            'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                            stateClasses,
                        ].join(' ')}
                    >
                        <StatusDot tone={s.tone} size={6} />
                        <span>{s.label}</span>
                        <span className="font-num text-xs text-content-muted">{count}</span>
                    </button>
                );
            })}
        </div>
    );
};

export default FilterBar;
